import { Timestamp } from 'firebase/firestore';
import moment from 'moment';

import type {
  ClientFirestoreTimestampPlain,
  CreateTotemParams,
  Totem,
  TotemFirestoreRecord,
} from '@src/models/interfaces/totem.interface';
import { DateUtil } from '@src/packages/utils/dates';

const toDate = (value: Parameters<typeof DateUtil.toDateOrNull>[0]): Date | null =>
  DateUtil.toDateOrNull(value);

export class TotemMapper {
  static computeIsExpired(
    startAt: Date | null,
    expiresIn: number | null,
    now: Date = new Date(),
  ): boolean {
    if (!startAt || expiresIn == null) {
      return false;
    }

    const expiresAt = moment(startAt).add(expiresIn, 'hours');
    return moment(now).isSameOrAfter(expiresAt);
  }

  static fromFirestore(id: string, record: TotemFirestoreRecord, now: Date = new Date()): Totem {
    const startAt = toDate(record.startAt);
    const expiresIn = record.expiresIn ?? null;
    const isExpired = TotemMapper.computeIsExpired(startAt, expiresIn, now);

    return {
      id,
      organizationId: record.organizationId,
      name: record.name.trim(),
      jti: record.jti,
      url: record.url,
      startAt,
      createdByUserId: record.uid ?? null,
      createdAt: toDate(record.createdAt),
      expiresIn,
      isExpired,
      isActive: !isExpired,
    };
  }

  static toCallableTimestamp(startDate: moment.Moment): ClientFirestoreTimestampPlain {
    const timestamp = Timestamp.fromMillis(startDate.valueOf());

    return {
      seconds: timestamp.seconds,
      nanoseconds: timestamp.nanoseconds,
    };
  }

  static toCreateTotemParams(
    organizationId: string,
    name: string,
    startAt: moment.Moment,
    expiresIn?: number | null,
  ): CreateTotemParams {
    const params: CreateTotemParams = {
      organizationId,
      name: name.trim(),
      startAt: TotemMapper.toCallableTimestamp(startAt),
    };

    if (expiresIn != null) {
      params.expiresIn = expiresIn;
    }

    return params;
  }
}
